import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import {
  ManagerProgressionTaskSlug,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import {
  ANY_SPORT_MY_CARDS_CARDS_TYPE,
  ANY_SPORT_PLAY,
} from '@sorare/core/src/constants/__generated__/routes';
import { ANY_SPORT_SERIES_MARKET_COMMON_PACKS } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useGetMyCardsBoostLink } from '@sorare/core/src/hooks/my-cards/useGetMyCardsBoostLink';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';

import type { useTaskButtonProps_taskInterface } from './__generated__/useTaskButtonProps.graphql.ts';

type Props = {
  task: useTaskButtonProps_taskInterface;
};
export const useTaskButtonProps = ({ task }: Props) => {
  const { lifecycle } = useLifecycle();
  const getMyCardsBoostLink = useGetMyCardsBoostLink();

  const { sport, generateSportContextPath } = useSportContext();

  if (task.name === ManagerProgressionTaskSlug.BUY_CARD_PACK) {
    return {
      label: <FormattedMessage {...glossary.buyNow} />,
      to: `${generateSportContextPath(ANY_SPORT_SERIES_MARKET_COMMON_PACKS)}#buy-packs`,
    };
  }

  if (task.name === ManagerProgressionTaskSlug.DROP_COMMON_CARD) {
    return {
      label: <FormattedMessage {...fantasy.scrap} />,
      to: `${generateSportContextPath(ANY_SPORT_MY_CARDS_CARDS_TYPE, {
        params: {
          type: Rarity.common,
        },
      })}${lifecycle?.[LIFECYCLE.sawDropOnboarding] ? '?selectionMode=drop' : ''}`,
    };
  }

  if (task.name === ManagerProgressionTaskSlug.LEVEL_UP_CARD) {
    return {
      label: <FormattedMessage {...fantasy.levelUp} />,
      to: getMyCardsBoostLink({
        rarity: Rarity.common,
        sport,
      }),
    };
  }

  if (task.name === ManagerProgressionTaskSlug.CLAIM_TASK) {
    return {
      label: <FormattedMessage {...glossary.claim} />,
      to: `${generateSportContextPath(ANY_SPORT_SERIES_MARKET_COMMON_PACKS)}`,
    };
  }

  if (task.name === ManagerProgressionTaskSlug.COMPOSE_TEAM) {
    return {
      label: <FormattedMessage {...glossary.submitLineup} />,
      to: generateSportContextPath(ANY_SPORT_PLAY),
    };
  }

  if (task.name === ManagerProgressionTaskSlug.COLLECT_CARD_BY_TIER) {
    return {
      label: (
        <FormattedMessage
          id="useTaskButtonProps.collectStar"
          defaultMessage="Collect Star Card"
        />
      ),
      to: `${generateSportContextPath(ANY_SPORT_SERIES_MARKET_COMMON_PACKS)}`,
    };
  }

  return {
    label: <FormattedMessage {...glossary.claim} />,
    to: null,
  };
};

useTaskButtonProps.fragments = {
  taskInterface: gql`
    fragment useTaskButtonProps_taskInterface on TaskInterface {
      id
      taskConfigSlug
      name
    }
  ` as TypedDocumentNode<useTaskButtonProps_taskInterface>,
};
